import ApiClient from "../ApiClient";
import {
  accountStageToApiValue,
  mapRawStageToAccountStage,
} from "../constants/accountStages";

export const AccountSourceApi = {
  WEBINAR: 1,
  QUALIFIED_LEAD: 2,
  DIRECT_OUTREACH: 3,
  REFERRAL: 4,
  COLD_CALL: 5,
  SOCIAL_MEDIA: 6,
};

const SOURCE_UI_TO_API = {
  webinar: AccountSourceApi.WEBINAR,
  qualifiedlead: AccountSourceApi.QUALIFIED_LEAD,
  "qualified lead": AccountSourceApi.QUALIFIED_LEAD,
  directoutreach: AccountSourceApi.DIRECT_OUTREACH,
  "direct outreach": AccountSourceApi.DIRECT_OUTREACH,
  referral: AccountSourceApi.REFERRAL,
  coldcall: AccountSourceApi.COLD_CALL,
  "cold call": AccountSourceApi.COLD_CALL,
  socialmedia: AccountSourceApi.SOCIAL_MEDIA,
  "social media": AccountSourceApi.SOCIAL_MEDIA,
};

const SOURCE_API_TO_UI = {
  [AccountSourceApi.WEBINAR]: "Webinar",
  [AccountSourceApi.QUALIFIED_LEAD]: "Qualified Lead",
  [AccountSourceApi.DIRECT_OUTREACH]: "Direct Outreach",
  [AccountSourceApi.REFERRAL]: "Referral",
  [AccountSourceApi.COLD_CALL]: "Cold Call",
  [AccountSourceApi.SOCIAL_MEDIA]: "Social Media",
};

function normalizeSourceKey(source) {
  return String(source ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

export function accountSourceToApiValue(source) {
  if (typeof source === "number") return source;
  const asNum = Number(source);
  if (Number.isFinite(asNum) && SOURCE_API_TO_UI[asNum]) return asNum;
  const key = normalizeSourceKey(source);
  return SOURCE_UI_TO_API[key] ?? AccountSourceApi.QUALIFIED_LEAD;
}

export function apiSourceToUiValue(source) {
  const n = typeof source === "number" ? source : Number(source);
  if (Number.isFinite(n) && SOURCE_API_TO_UI[n]) return SOURCE_API_TO_UI[n];
  const key = normalizeSourceKey(source);
  if (SOURCE_UI_TO_API[key] != null) return SOURCE_API_TO_UI[SOURCE_UI_TO_API[key]];
  return source || "—";
}

const PAYMENT_METHOD_API_MAP = {
  "bank transfer": "netbanking",
  "net banking": "netbanking",
  netbanking: "netbanking",
  upi: "upi",
  "credit card": "card",
  card: "card",
  cash: "cash",
  other: "other",
};

export function mapUiPaymentMethodToApi(method) {
  const key = String(method ?? "")
    .trim()
    .toLowerCase();
  return PAYMENT_METHOD_API_MAP[key] || "netbanking";
}

export function mapApiPaymentMethodToUi(method) {
  const key = String(method ?? "")
    .trim()
    .toLowerCase();
  if (key === "netbanking") return "Bank Transfer";
  if (key === "upi") return "UPI";
  if (key === "card" || key === "creditcard") return "Credit Card";
  if (key === "cash") return "Cash";
  return method || "—";
}

export function formatJoinedDate(value) {
  if (!value || value === "—") return "—";
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return raw;
}

export function normalizeCustomerAccountRow(row, index = 0) {
  const amount = Number(
    String(row.initialValue ?? row.revenue ?? row.amount ?? "").replace(/[^0-9.]/g, ""),
  );
  const joinedRaw =
    row.joinedOn ??
    row.joined ??
    row.activeFrom ??
    row.createdOn ??
    row.accountCreatedOn ??
    "";
  const joined = formatJoinedDate(joinedRaw);

  return {
    accountId: String(row.id ?? row.accountId ?? row.customerAccountId ?? `account-${index}`),
    company: row.accountName ?? row.company ?? row.Company ?? "—",
    contactName: row.primaryContact ?? row.contactName ?? row.ContactName ?? "—",
    email: row.companyEmail ?? row.email ?? row.contactEmail ?? row.Email ?? "—",
    phone: row.phoneNumber ?? row.phone ?? row.Phone ?? "—",
    source: apiSourceToUiValue(row.source ?? row.Source ?? row.sourceName),
    stage: mapRawStageToAccountStage(
      row.stage ?? row.accountStage ?? row.stageName ?? row.Stage,
    ),
    cashInflow:
      row.cashInflow ||
      row.revenue ||
      (Number.isFinite(amount) && amount > 0
        ? `₹${amount.toLocaleString("en-IN")}`
        : "—"),
    revenue: row.revenue ? String(row.revenue) : Number.isFinite(amount) ? String(amount) : "—",
    joined,
    ConvertedFromLead: row.ConvertedFromLead ?? row.convertedFromLead ?? "",
    linkedLead:
      row.linkedLead ??
      row.linkedLeadName ??
      row.ConvertedFromLead ??
      row.convertedFromLead ??
      "—",
    leadId: String(row.leadId ?? row.linkedLeadId ?? ""),
    notes: row.notes ?? row.Notes ?? "",
  };
}

export function parseDashboardAccountsResponse(resp) {
  const payload = resp?.data?.data ?? resp?.data ?? {};
  let rows = [];

  if (Array.isArray(payload.stageGroups)) {
    rows = payload.stageGroups.flatMap((group) => {
      const accounts = Array.isArray(group.accounts) ? group.accounts : [];
      const groupStage = group.stage ?? group.accountStage ?? group.stageName;
      return accounts.map((acc) => ({
        ...acc,
        stage: acc.stage ?? acc.accountStage ?? groupStage,
      }));
    });
  } else if (Array.isArray(payload.items)) {
    rows = payload.items;
  } else if (Array.isArray(payload.accounts)) {
    rows = payload.accounts;
  } else if (Array.isArray(payload.rows)) {
    rows = payload.rows;
  } else if (Array.isArray(payload)) {
    rows = payload;
  }

  return rows.map(normalizeCustomerAccountRow);
}

function formatTxDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, "0");
    const mon = d.toLocaleDateString("en-GB", { month: "short" });
    const yr = String(d.getFullYear()).slice(-2);
    return `${day} ${mon} ${yr}`;
  }
  return String(value);
}

export function formatTotalAmount(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN")}`;
}

export function normalizeApiTransaction(row, index = 0) {
  const rawAmount = row.amount ?? row.Amount ?? 0;
  const amountNum =
    typeof rawAmount === "number"
      ? rawAmount
      : Number(String(rawAmount).replace(/[^0-9.-]/g, ""));
  return {
    id: row.id ?? row.transactionId ?? `tx-${index}`,
    description: row.description ?? row.Description ?? "—",
    amount: Number.isFinite(amountNum) ? amountNum : 0,
    amountDisplay:
      Number.isFinite(amountNum) && amountNum > 0
        ? `₹${amountNum.toLocaleString("en-IN")}`
        : "—",
    date: formatTxDate(row.paidOn ?? row.transactionDate ?? row.date ?? row.createdOn),
    method: mapApiPaymentMethodToUi(row.paymentMethod ?? row.PaymentMethod),
    note: row.notes ?? row.note ?? row.Notes ?? "—",
    receiptNo: row.receiptNo ?? "",
    status: row.status ?? "Success",
    source: row.source ?? "manual",
  };
}

export function parseTransactionListResponse(resp) {
  const payload = resp?.data?.data ?? resp?.data ?? {};
  const items =
    payload.items ??
    payload.transactions ??
    (Array.isArray(payload) ? payload : []);
  const totalCount = Number(payload.totalCount ?? 0);
  const rawPage = Number(payload.pageNumber ?? 1);
  const pageSize = Number(payload.pageSize ?? 5);
  const totalAmount = Number(payload.totalAmount ?? 0);

  return {
    transactions: (Array.isArray(items) ? items : []).map(normalizeApiTransaction),
    totalCount: Number.isFinite(totalCount) ? totalCount : 0,
    pageNumber: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 5,
    totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
  };
}

export function parseActivityListResponse(resp) {
  const payload = resp?.data?.data ?? {};
  const items = payload.items ??(Array.isArray(payload) ? payload : []);
  const totalCount = Number(payload.totalCount ?? 0);
  const rawPage = Number(payload.pageNumber ?? 1);
  const pageSize = Number(payload.pageSize ?? 5);

  return {
    activities: (Array.isArray(items) ? items : []).map(normalizeApiActivity),
    totalCount: Number.isFinite(totalCount) ? totalCount : 0,
    pageNumber: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 5,
  };
}

export function normalizeApiActivity(row, index = 0) {
  const amountNum = Number(row.amount ?? 0);
  const activityDate = row.activityDate ?? row.date;
  let dateLabel = "—";
  if (activityDate) {
    const d = new Date(activityDate);
    if (!Number.isNaN(d.getTime())) {
      dateLabel = d.toLocaleDateString("en-GB", { month: "short", day: "numeric" });
    }
  }
  return {
    id: row.id ?? `activity-${index}`,
    dateLabel,
    title: row.title ?? "—",
    detail: row.description ?? row.detail ?? "",
    amount: amountNum > 0 ? `₹${amountNum.toLocaleString("en-IN")}` : "",
  };
}

export function buildAddActivityTimelinePayload({
  customerAccountId,
  activityDate,
  title,
  description,
  amount,
}) {
  if (!customerAccountId) {
    throw new Error("Customer account id is required to add activity.");
  }
  return {
    customerAccountId,
    activityDate: activityDate || new Date().toISOString(),
    title: title || "",
    description: description || "",
    amount: Number(amount) || 0,
  };
}

export function buildAddTransactionPayload({
  account,
  transactionId,
  description,
  amount,
  paymentMethod,
  notes,
  receiptNo,
  paidOn,
}) {
  const leadId = account?.leadId || account?.linkedLeadId;
  if (!leadId) {
    throw new Error("This account has no linked lead. Link a lead before adding transactions.");
  }
  return {
    id: transactionId,
    leadId,
    amount: Number(amount),
    paymentMethod: mapUiPaymentMethodToApi(paymentMethod),
    notes: notes || "",
    description: description || "",
    receiptNo: receiptNo?.trim() || "",
    paidOn: paidOn || new Date().toISOString(),
  };
}

const CustomerAccountService = {
  async createCustomerAccount(payload) {
    const body = {
      ...payload,
      stage:
        typeof payload.stage === "number"
          ? payload.stage
          : accountStageToApiValue(payload.stage),
      source: accountSourceToApiValue(payload.source),
    };
    return ApiClient.post(`/CustomerAccount/add-customer-account`, body);
  },

  async updateCustomerAccount(payload) {
    const body = {
      ...payload,
      stage:
        payload.stage != null && typeof payload.stage !== "number"
          ? accountStageToApiValue(payload.stage)
          : payload.stage,
      source: accountSourceToApiValue(payload.source),
    };
    return ApiClient.put(`/CustomerAccount/update-customer-account`, body);
  },

  async updateAccountStage({ accountId, stage, accountStage } = {}) {
    const uiStage = accountStage ?? stage;
    const payload = {
      id: accountId,
      stage: accountStageToApiValue(uiStage),
    };
    return ApiClient.patch(`/CustomerAccount/update-account-stage`, payload);
  },

  async getCustomerAccounts() {
    return ApiClient.get(`/CustomerAccount/account-dashboard`);
  },

  async getCustomerAccountById(id) {
    return ApiClient.get(`/CustomerAccount/customer-account`, {
      params: { id },
    });
  },

  async deleteCustomerAccount(id) {
    return ApiClient.delete(`/CustomerAccount/delete-customer-account/${id}`);
  },

  async createActivityTimeline(payload) {
    return ApiClient.post(`/CustomerAccount/add-activity-timeline`, payload);
  },

  async getActivityTimelines(accountId, { pageNumber = 1, pageSize = 5 } = {}) {
    return ApiClient.get(`/CustomerAccount/activity-timelines/${accountId}`, {
      params: { pageNumber, pageSize },
    });
  },

  async addTransaction(payload) {
    return ApiClient.post(`/CustomerAccount/add-transaction`, payload);
  },

  async updateTransaction(payload) {
    return ApiClient.put(`/CustomerAccount/update-transaction`, payload);
  },

  async getTransactions(leadId, { pageNumber = 1, pageSize = 5 } = {}) {
    return ApiClient.get(`/CustomerAccount/transaction-list/${leadId}`, {
      params: { pageNumber, pageSize },
    });
  },

  async deleteTransaction(id) {
    return ApiClient.delete(`/CustomerAccount/delete-transaction/${id}`);
  },

  async updateAccountStatus({ leadId, status } = {}) {
    const payload = {
      leadId: leadId,
      status: status,
    };
    return ApiClient.patch(`/CustomerAccount/update-account-status`, payload);
  },
};

export default CustomerAccountService;
